/**
 * The device / zoom / orientation controls both preview toolbars wear.
 *
 * The version editor and the snapshot editor run the same engine, so a frame
 * picked in one has to mean the same thing in the other: the figures are real
 * device pixels the playable lays itself out for, and the zoom is only how much
 * of the pane that frame is shown at. Sharing the controls is what keeps the two
 * editors from drifting into different ideas of what "iPhone SE" is.
 */

import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip
} from "@material-ui/core";
import StayCurrentPortraitIcon from "@material-ui/icons/StayCurrentPortrait";
import StayCurrentLandscapeIcon from "@material-ui/icons/StayCurrentLandscape";

import { ZOOM_FIT } from "../modules/previewFrame";
import { PANEL_BG } from "./snapshotEditor/panelPalette";

/**
 * The row that opens the size dialog. Not a device id — the select's own
 * "custom" state is the empty value, which a dragged frame already uses.
 */
const CUSTOM_SIZE_ROW = "__customSize__";

/** The drag handles clamp to the same floor, so typing cannot beat dragging. */
const MIN_SIDE = 120;
const MAX_SIDE = 4096;

const useStyles = makeStyles({
  deviceSelect: {
    fontSize: "12.5px"
  },
  deviceDims: {
    marginLeft: "6px",
    opacity: 0.6,
    fontSize: "11.5px"
  },
  zoomSelect: {
    fontSize: "12.5px",
    marginLeft: "4px"
  },
  sizeFields: { display: "flex", alignItems: "center", gap: "8px" },
  sizeTimes: { opacity: 0.6 }
});

/** 0.5 → "0.5×", 1 → "1×" — trailing zeroes only where they carry something. */
function formatZoom(value) {
  const rounded = Math.round(value * 100) / 100;
  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(2).replace(/0$/, "")}×`;
}

export default function PreviewDeviceControls({
  isPortrait,
  devices,
  deviceId,
  isCustomSize,
  previewWidth,
  previewHeight,
  zoom,
  zoomSteps,
  effectiveZoom,
  onSelectZoom,
  onSelectDevice,
  onToggleOrientation,
  onCustomSize
}) {
  const classes = useStyles();
  const [isSizeDialogOpen, setIsSizeDialogOpen] = useState(false);
  const [draftWidth, setDraftWidth] = useState("");
  const [draftHeight, setDraftHeight] = useState("");
  const deviceList = devices || [];
  const selected = deviceList.find((device) => device.id === deviceId) || deviceList[0];
  const zoomList = zoomSteps || [];

  // Fit is the empty value for the same reason Custom size is below: it has to
  // be selectable again after a step was picked, and re-picking the step that is
  // already selected has to stay a no-op.
  const zoomValue = zoom === ZOOM_FIT ? "" : zoom;

  // An empty value is what puts the select into its "Custom size" state. It also
  // means every device row counts as a change, so picking the device you had
  // before dragging snaps back to it — with the id still selected it would be a
  // no-op and the frame would stay stuck at the dragged size.
  const value = isCustomSize ? "" : selected ? selected.id : "";

  // Opens on the size that is already on screen, so typing one side and
  // keeping the other is the common case it costs nothing.
  function openSizeDialog() {
    setDraftWidth(String(Math.round(previewWidth) || ""));
    setDraftHeight(String(Math.round(previewHeight) || ""));
    setIsSizeDialogOpen(true);
  }

  const width = Number(draftWidth);
  const height = Number(draftHeight);
  const isDraftUsable =
    Number.isFinite(width) &&
    Number.isFinite(height) &&
    width >= MIN_SIDE &&
    height >= MIN_SIDE &&
    width <= MAX_SIDE &&
    height <= MAX_SIDE;

  function applySize() {
    if (!isDraftUsable) return;
    onCustomSize({ width: Math.round(width), height: Math.round(height) });
    setIsSizeDialogOpen(false);
  }

  return (
    <>
      {deviceList.length ? (
        <Select
          value={value}
          onChange={(event) => {
            if (event.target.value === CUSTOM_SIZE_ROW) {
              openSizeDialog();
              return;
            }
            onSelectDevice(event.target.value);
          }}
          disableUnderline
          displayEmpty
          className={classes.deviceSelect}
          // The figures always describe the frame as it is on screen right
          // now: the orientation toggle swaps a preset's pair, and a dragged
          // frame reports its own measurements.
          renderValue={(selectedId) => {
            if (!selectedId) {
              return (
                <span>
                  Custom size
                  <span className={classes.deviceDims}>
                    {Math.round(previewWidth)}×{Math.round(previewHeight)}
                  </span>
                </span>
              );
            }
            const device = deviceList.find((item) => item.id === selectedId);
            if (!device) return "";
            const width = isPortrait ? device.width : device.height;
            const height = isPortrait ? device.height : device.width;
            return (
              <span>
                {device.label}
                <span className={classes.deviceDims}>
                  {width}×{height}
                </span>
              </span>
            );
          }}
        >
          {deviceList.map((device) => (
            <MenuItem key={device.id} value={device.id} className={classes.deviceSelect}>
              {device.label}
              <span className={classes.deviceDims}>
                {device.width}×{device.height}
              </span>
            </MenuItem>
          ))}
          {onCustomSize ? (
            <MenuItem value={CUSTOM_SIZE_ROW} className={classes.deviceSelect}>
              Custom size…
            </MenuItem>
          ) : null}
        </Select>
      ) : null}

      {/* Dragging the handles reaches any size too; this is for the sizes people
          already know the numbers for. */}
      <Dialog
        open={isSizeDialogOpen}
        onClose={() => setIsSizeDialogOpen(false)}
        PaperProps={{ style: { backgroundColor: PANEL_BG } }}
      >
        <DialogTitle>Custom size</DialogTitle>
        <DialogContent>
          <div className={classes.sizeFields}>
            <TextField
              autoFocus
              type="number"
              label="Width"
              size="small"
              value={draftWidth}
              inputProps={{ min: MIN_SIDE, max: MAX_SIDE }}
              onChange={(event) => setDraftWidth(event.target.value)}
              onKeyDown={(event) => event.key === "Enter" && applySize()}
            />
            <span className={classes.sizeTimes}>×</span>
            <TextField
              type="number"
              label="Height"
              size="small"
              value={draftHeight}
              inputProps={{ min: MIN_SIDE, max: MAX_SIDE }}
              onChange={(event) => setDraftHeight(event.target.value)}
              onKeyDown={(event) => event.key === "Enter" && applySize()}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsSizeDialogOpen(false)}>Cancel</Button>
          <Button color="primary" disabled={!isDraftUsable} onClick={applySize}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {zoomList.length ? (
        <Select
          value={zoomValue}
          onChange={(event) => onSelectZoom(event.target.value === "" ? ZOOM_FIT : event.target.value)}
          disableUnderline
          displayEmpty
          className={classes.zoomSelect}
          renderValue={(picked) =>
            picked === "" ? (
              <span>
                Fit
                <span className={classes.deviceDims}>{formatZoom(effectiveZoom)}</span>
              </span>
            ) : (
              formatZoom(picked)
            )
          }
        >
          <MenuItem value="" className={classes.zoomSelect}>
            Fit
          </MenuItem>
          {/* A drag under Fit pins the zoom to the scale that was on screen,
              which is its own number — carry it so the select has something to
              match and stays in range. */}
          {zoomValue !== "" && !zoomList.includes(zoomValue) ? (
            <MenuItem value={zoomValue} className={classes.zoomSelect}>
              {formatZoom(zoomValue)}
            </MenuItem>
          ) : null}
          {zoomList.map((step) => (
            <MenuItem key={step} value={step} className={classes.zoomSelect}>
              {formatZoom(step)}
            </MenuItem>
          ))}
        </Select>
      ) : null}

      <Tooltip title={isPortrait ? "Switch to landscape" : "Switch to portrait"}>
        <IconButton size="small" onClick={onToggleOrientation}>
          {isPortrait ? <StayCurrentPortraitIcon fontSize="small" /> : <StayCurrentLandscapeIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
    </>
  );
}
